/**
 * Does the Day-grid block have room for its payment badge? (DNKADM-0393 U3)
 *
 * The badge is the block's LAST child in a `flex column, gap 2px, overflow: hidden`
 * box, so it can never overlap the member's name — that part is structural. But
 * `mt: 'auto'` resolves to 0 once the content fills the box, and the badge is then
 * what `overflow: hidden` cuts: a half-cut pill rather than a clean absence. So the
 * caller checks the fit before rendering it at all.
 *
 * The rows are measured from what the block ACTUALLY renders for that booking, not
 * from an assumed stack: `serviceCoachLine` and `moneyLine` are each present or not
 * per reservation, and assuming both (or neither) is exactly what makes a fixed
 * threshold wrong for most bookings.
 *
 * A pure module (the `derive.ts` / `contrastPolicy.ts` sibling pattern) so the
 * arithmetic is unit-testable: jsdom has no layout, so a test that rendered the block
 * could not tell a clipped badge from a whole one.
 *
 * The PORTRAIT layout uses this as a render-time check — there a block's height is its
 * duration. A landscape block's height comes from the lane and is not knowable at render
 * time, so `DayGrid.tsx`'s `laneMinHeight` instead uses the same row constants to size the
 * lane's minimum height so the badge always has a seat (DNKADM-0456 V-73).
 */

/** Block padding, `p: '6px 8px'` top + bottom. */
const BLOCK_PADDING_Y = 12;
/** The flex column's `gap: '2px'`, once per row after the first. */
const BLOCK_ROW_GAP = 2;
/** The name row (`fontSize: 13.5, lineHeight: 1.2`). */
const BLOCK_NAME_ROW_HEIGHT = 16;
/** The service/coach row (`fontSize: 11.5`), when `serviceCoachLine` is non-null. */
const BLOCK_META_ROW_HEIGHT = 16;
/** The money row (`fontSize: 10.5`), when `moneyLine` is non-null. */
const BLOCK_MONEY_ROW_HEIGHT = 15;
/** The badge itself (`fontSize: 9, lineHeight: 1.5` + `py: '1px'`). */
const PAYMENT_BADGE_HEIGHT = 16;

/**
 * The block height (px) needed to seat the payment badge UNDER the rows this
 * particular booking renders. Below it the badge is dropped rather than clipped into
 * a sliver; the block's own `aria-label` still announces the payment state, so only
 * the glance is lost.
 *
 * If the block's typography changes, the constants above are the one place to revisit.
 */
export function paymentBadgeMinBlockHeight(hasMetaRow: boolean, hasMoneyRow: boolean): number {
  return (
    BLOCK_PADDING_Y +
    BLOCK_NAME_ROW_HEIGHT +
    (hasMetaRow ? BLOCK_ROW_GAP + BLOCK_META_ROW_HEIGHT : 0) +
    (hasMoneyRow ? BLOCK_ROW_GAP + BLOCK_MONEY_ROW_HEIGHT : 0) +
    BLOCK_ROW_GAP +
    PAYMENT_BADGE_HEIGHT
  );
}
